/* eslint-disable import/order */
import { ErrorInfo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AppProps } from "next/app";
import { useRouter } from "next/router";

import { isEmpty } from "@swiggy-private/js-utils";
import { mockWebSdk } from "@swiggy-private/native-bridge";
import { ErrorPage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ErrorPage";
import GlobalStyled from "@swiggy-private/aatm-nirbhar-ui/dist/lib/GlobalStyled";
import { NewRelicErrorBoundary } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/NewRelic";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Base";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";

import type { GetConfigValueResponsePayload } from "@swiggy-private/native-bridge/dist/interfaces";
import { RouterContext, SwiggyHeadersContext, GiftPPIContext } from "@swiggy-private/swiggy-money-context/src";
import type { ISwiggyHeaders } from "@swiggy-private/swiggy-money-context/src/interfaces";
import { useBootstrap } from "hooks/useBootstrap";
import { useEffectOnce } from "hooks/useEffectOnce";
import { GlobalStyle, ShuttleAppName } from "styles/globals";
import { initBridgeSdk } from "utils/bridgeSdk";
import { getSwiggyHeadersFromNB, closeWebView, getDynamicFlags } from "utils/NativeBridge";
import { sendErrorBoundaryEvent } from "utils/newrelic";
import { isApp, isBrowserEnv, isPreviousRouteExistsInStack } from "utils/utils";
import { NEW_RELIC_APPLICATION_ID, PORTAL_SWIGGY_MONEY } from "../constants";

// generic hook to maintain prevous route in stack
const usePreviousRoute = () => {
    const router = useRouter();
    const ref = useRef<string | null>(null);

    useEffect(() => {
        if (!router || !router.events) return;

        const handleRouteChange = () => {
            ref.current = router.asPath;
        };

        router.events.on("routeChangeStart", handleRouteChange);

        return () => {
            router.events.off("routeChangeStart", handleRouteChange);
        };
    }, [router]);

    return ref.current;
};

const CLOSE = "click here to close";

function MyApp({ Component, pageProps }: AppProps) {
    useBootstrap(PORTAL_SWIGGY_MONEY);
    const sdk = useMemo(() => initBridgeSdk(), []);
    const [giftPPIFlags, setGiftPPIFlags] = useState({ giftPPI: false });
    const [swiggyHeaders, setSwiggyHeaders] = useState<ISwiggyHeaders | undefined>();
    const [dynamicFlags, setDynamicFlags] = useState<GetConfigValueResponsePayload>({});

    const router = useRouter();
    const previousRoute = usePreviousRoute();
    const isPreviousPathExists = isPreviousRouteExistsInStack();

    const onError = useCallback(
        (error?: Error, errorInfo?: ErrorInfo) => {
            sendErrorBoundaryEvent(error, errorInfo, swiggyHeaders);
        },
        [swiggyHeaders],
    );

    const handleCloseWebView = () => {
        closeWebView(sdk);
    };

    useEffectOnce(() => {
        if (isBrowserEnv() && process.env.NODE_ENV !== "production") {
            mockWebSdk();
        }

        if (typeof window !== "undefined") {
            window.keyboardStateChanged = function () {
                // do nothing...
            };

            window.onScreenVisibility = function () {
                // do nothing...
            };
        }
    });

    useEffect(() => {
        async function initApp() {
            try {
                if (isApp()) {
                    const headers = await getSwiggyHeadersFromNB(sdk);
                    const wpFlags = await getDynamicFlags(sdk);
                    setSwiggyHeaders(headers as ISwiggyHeaders);
                    setDynamicFlags(wpFlags || {});
                } else {
                    const smHeaders = localStorage.getItem("sm_headers") || "";
                    if (!isEmpty(smHeaders)) setSwiggyHeaders(JSON.parse(smHeaders));
                }
            } catch (error) {
                sendErrorBoundaryEvent(error as Error);
                closeWebView(sdk);
            }
        }
        initApp();
    }, [sdk]);

    const ErrorComponentWrapper = () => <ErrorPage onCtaClick={handleCloseWebView} ctaText={CLOSE} />;

    return (
        <NewRelicErrorBoundary
            applicationID={NEW_RELIC_APPLICATION_ID}
            onErrorCapture={onError}
            ErrorComponent={ErrorComponentWrapper}
        >
            <ThemeProvider theme={BaseTheme} colorMode="light">
                <ShuttleAppName data-app-name={`${process.env.NEXT_PUBLIC_APP_NAME}`} />
                <GlobalStyle />
                <GlobalStyled />
                <FontStyle />
                <SwiggyHeadersContext.Provider
                    value={{
                        swiggyHeaders: swiggyHeaders as ISwiggyHeaders,
                        setSwiggyHeaders,
                        dynamicFlags,
                        setDynamicFlags,
                    }}
                >
                    <RouterContext.Provider value={{ router, previousRoute, isPreviousPathExists }}>
                        <GiftPPIContext.Provider value={{ giftPPIFlags, setGiftPPIFlags }}>
                            <Component {...pageProps} />
                        </GiftPPIContext.Provider>
                    </RouterContext.Provider>
                </SwiggyHeadersContext.Provider>
            </ThemeProvider>
        </NewRelicErrorBoundary>
    );
}

export default MyApp;
